/**
 * Shared starter prompt definitions used by the Rovo Space landing page
 * (fullscreen), the Rovo chat sidebar, and the For You panel space cards.
 *
 * The 4 starters here match the canonical "green dot" (🟢) spec from:
 * https://hello.atlassian.net/wiki/spaces/proai/pages/6680743926/Starter+prompts+for+Projects+Spaces
 *
 * All consumers (rovo-for-you space cards, conversation-assistant side navigation,
 * and the SpaceLandingPage) import from this single canonical file so that a user
 * sees the same suggestions regardless of entry point.
 */
import React, { useMemo } from 'react';

import { type IntlShape, useIntl } from 'react-intl';

import AiGenerativeChaptersIcon from '@atlaskit/icon-lab/core/ai-generative-chapters';
import SunsetIcon from '@atlaskit/icon-lab/core/sunset';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import type { EmptyChatLaunchAction } from '@atlassian/conversation-assistant-store/controllers/ui-store';

import messages from './messages';

export type UseSpaceStartersParams = {
	/** Space id — unused by current starters but kept for API forwards-compatibility. */
	spaceId?: string;
	/** Space name — unused by current starters but kept for API forwards-compatibility. */
	spaceName?: string;
};

/**
 * Build the 4 canonical space starters as fully-formed `EmptyChatLaunchAction[]`.
 *
 * Useful when starters need to be built imperatively (e.g. inside a click
 * handler at the moment a space is selected, before `selectedSpace` state
 * updates). Prefer `useSpaceStarters` inside component bodies.
 */
export const buildSpaceStarters = (
	intl: Pick<IntlShape, 'formatMessage'>,
	// Params accepted for forwards-compatibility; currently unused.
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	_params: UseSpaceStartersParams = {},
): EmptyChatLaunchAction[] => {
	const { formatMessage } = intl;

	return [
		{
			id: 'get-daily-brief',
			title: formatMessage(messages.getDailyBriefTitle),
			icon: () => <SunsetIcon label="" />,
			handleType: 'Skill',
			type: 'skill-action',
			staticPrompt: '/get-workstream-brief ',
		},
		{
			id: 'get-project-overview',
			title: formatMessage(messages.getProjectOverviewTitle),
			icon: () => <AiGenerativeChaptersIcon label="" />,
			handleType: 'Skill',
			type: 'skill-action',
			staticPrompt: '/get-project-details ',
		},
		{
			id: 'find-my-action-items',
			title: formatMessage(messages.findMyActionItemsTitle),
			icon: () => <AiChatIcon label="" />,
			handleType: 'Chat',
			type: 'convo-starter-action',
			staticPrompt: formatMessage(messages.findMyActionItemsPrompt),
		},
		{
			id: 'prep-for-checkin',
			title: formatMessage(messages.prepForCheckinTitle),
			icon: () => <AiChatIcon label="" />,
			handleType: 'Chat',
			type: 'convo-starter-action',
			staticPrompt: formatMessage(messages.prepForCheckinPrompt),
		},
	];
};

/**
 * Hook returning the 4 canonical space starters as fully-formed
 * `EmptyChatLaunchAction[]`, ready to be passed to
 * `setEmptyChatLaunch({ customConversationActions })` or rendered as starter
 * cards on the Space landing page.
 */
export const useSpaceStarters = (params: UseSpaceStartersParams = {}): EmptyChatLaunchAction[] => {
	const intl = useIntl();
	const { spaceId, spaceName } = params;

	return useMemo(
		() => buildSpaceStarters(intl, { spaceId, spaceName }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[intl.formatMessage, spaceId, spaceName],
	);
};
